import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  ReactNode,
} from "react"
import {
  useNotificationsList,
  useNotificationSummary,
  useMarkNotificationsRead,
} from "../hooks/queries/useNotifications"
import { type Notification } from "../services/daeApi"
import { useAuth } from "./AuthContext"

/**
 * An ephemeral toast message displayed in the bottom-right corner. Toasts auto-dismiss after a
 * timeout and can be manually dismissed by the user.
 */
export interface Toast {
  /**
   * Unique identifier for this toast instance.
   */
  id: string

  /**
   * Bold heading text shown at the top of the toast.
   */
  subject: string

  /**
   * Optional body content shown below the subject. Usually a string, but may be a node (e.g. a
   * bulleted list of validation failures) so callers can render multi-line detail.
   */
  message?: ReactNode

  /**
   * Controls the left border accent color (error=red, warning=amber, success=green,
   * default=primary).
   */
  variant: "success" | "error" | "warning" | "default"

  /**
   * Optional navigation links rendered at the bottom of the toast.
   */
  actions?: { label: string; to: string }[]

  /**
   * Called when the user clicks the toast body (before navigation, if actions exist).
   */
  onClick?: () => void
}

/**
 * Options for creating a new toast. Same as Toast but without the auto-generated ID.
 */
export type ShowToastOptions = Omit<Toast, "id">

interface NotificationContextType {
  /**
   * All loaded notifications for the current user. Initially the first page (100), expanding as
   * consumers (e.g. NotificationsPage) fetch additional pages from the shared query.
   */
  notifications: Notification[]

  /**
   * Server-computed count of unread notifications for the bell badge. Sourced from the notification
   * summary endpoint, not derived from the loaded array.
   */
  unreadCount: number

  /**
   * Whether notifications are currently being fetched. True during initial load and during manual
   * refresh (not polling).
   */
  loading: boolean

  /**
   * Manually refresh notifications from the API.
   */
  refresh: () => Promise<void>

  /**
   * Mark a notification as read. Updates local state optimistically.
   */
  markAsRead: (notificationId: string) => void

  /**
   * Active toasts to display.
   */
  toasts: Toast[]

  /**
   * Show an ephemeral toast. Returns the toast ID.
   */
  showToast: (options: ShowToastOptions) => string

  /**
   * Dismiss a single toast by ID.
   */
  dismissToast: (id: string) => void

  /**
   * Dismiss all active toasts.
   */
  dismissAllToasts: () => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

/**
 * Hook to access shared notifications and toast API from the NotificationContext.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook must be co-located with context
export function useNotifications() {
  const context = useContext(NotificationContext)

  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider")
  }

  return context
}

/**
 * Provider component that manages notifications (fetching, polling, read state) and ephemeral
 * toasts (show/dismiss). Any component can call showToast to display a toast; new notifications
 * detected during polling are automatically shown as toasts.
 */
export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const daeId = user?.isAuthenticated ? user.principalId : undefined
  const [toasts, setToasts] = useState<Toast[]>([])
  const knownIdsRef = useRef<Set<string>>(new Set())

  const { data, isLoading: loading, refetch } = useNotificationsList(daeId)

  // The bell polls a cheap summary endpoint for counts + the newest notification id.
  const { data: summary } = useNotificationSummary(daeId)
  const unreadCount = summary?.unreadCount ?? 0

  // Fetch page 1 of the item list only when the newest notification changes (something new
  // arrived), rather than polling the list. Toast detection below diffs the refreshed page 1
  // against the baseline. Page 1 (100 items) caps how many new items one poll can surface,
  // which is far beyond any realistic 10s burst - so no multi-page walk is needed.
  const lastSeenLatestIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const latestId = summary?.latestId
    if (!latestId) return
    if (lastSeenLatestIdRef.current === latestId) return
    lastSeenLatestIdRef.current = latestId
    void refetch()
  }, [summary?.latestId, refetch])

  const notifications = useMemo(() => data?.pages.flatMap((p) => p.notifications) ?? [], [data])
  const markReadMutation = useMarkNotificationsRead(daeId)
  const markReadMutateRef = useRef(markReadMutation.mutate)
  markReadMutateRef.current = markReadMutation.mutate

  const showToast = useCallback((options: ShowToastOptions): string => {
    const id = crypto.randomUUID()
    setToasts((prev) => [...prev, { ...options, id }])
    return id
  }, [])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const dismissAllToasts = useCallback(() => {
    setToasts([])
  }, [])

  const markAsRead = useCallback((notificationId: string) => {
    markReadMutateRef.current([notificationId])
  }, [])

  const refresh = useCallback(async () => {
    await refetch()
  }, [refetch])

  // Detect new notifications and show as toasts. Establish the baseline from the first loaded
  // page; subsequent polls surface anything newer (which appears at the top, newest-first).
  const initialLoadDoneRef = useRef(false)

  useEffect(() => {
    if (notifications.length === 0) return

    if (!initialLoadDoneRef.current) {
      knownIdsRef.current = new Set(notifications.map((n) => n.notification_id))
      initialLoadDoneRef.current = true
      return
    }

    const newOnes = notifications.filter((n) => !knownIdsRef.current.has(n.notification_id))
    for (const n of newOnes) {
      showToast({
        subject: n.subject || `Study ${n.study_id || "Update"}`,
        message: n.message,
        variant: getNotificationVariant(n.notification_type),
        onClick: () => markAsRead(n.notification_id),
      })
    }

    // Union new ids into the baseline so already-toasted items are never re-toasted
    // when page 1 refreshes again.
    for (const n of notifications) knownIdsRef.current.add(n.notification_id)
  }, [notifications, showToast, markAsRead])

  const contextValue = useMemo(
    () => ({
      notifications,
      unreadCount,
      loading,
      refresh,
      markAsRead,
      toasts,
      showToast,
      dismissToast,
      dismissAllToasts,
    }),
    [
      notifications,
      unreadCount,
      loading,
      refresh,
      markAsRead,
      toasts,
      showToast,
      dismissToast,
      dismissAllToasts,
    ],
  )

  return (
    <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
  )
}

const NOTIFICATION_VARIANTS: Record<string, Toast["variant"]> = {
  INGESTION_RUN_FAILED_REVIEW: "warning",
  DTA_INACTIVE: "warning",
  DTA_REJECTION: "error",
  DTA_EXTRACTION_FAILED: "error",
  PROTOCOL_EXTRACTION_FAILED: "error",
  PROPOSAL_REJECTED: "error",
  CONNECTION_REQUEST_REJECTED: "error",
  EMAIL_DELIVERY_FAILURE: "error",
  SECURITY_ALERT: "error",
  INGESTION_RUN_COMPLETED: "success",
  PROPOSAL_ACCEPTED: "success",
  DTA_ACTIVE: "success",
  DTA_EXTRACTION_COMPLETE: "success",
  PROTOCOL_EXTRACTION_COMPLETE: "success",
  CONNECTION_REQUEST_APPROVED: "success",
}

function getNotificationVariant(type?: string): Toast["variant"] {
  return (type && NOTIFICATION_VARIANTS[type]) || "default"
}
